import { useState } from "react";
import "./TradesTable.css"
import TablePagination from "../NewPagination/NewPagination";

// ─── Helper: safely read a dot-path from an object ───────────────────────────
function getByPath(obj, path) {
  if (!path) return undefined;
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

const VerifiedTradesTable = ({
  trades        = [],
  isCommunity   = false,
  loading       = false,
  onRowClick,
  columns       = [],
  itemsPerPage  = 10,
  noDataMessage = "No verified trades yet",
  nameKey       = "statsSnapshot.name",
  idKey         = "_id",
  ordersKey     = "statsSnapshot.orders",
}) => {
  const [openId, setOpenId]           = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const showNameCol = Boolean(nameKey);

  const isOpen = (id) => openId === id;
  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  const totalPages = Math.ceil(trades.length / itemsPerPage);
  const paginated  = trades.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (p) => {
    setCurrentPage(p);
    setOpenId(null);
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="loadingWrapper">
        <div className="loading-4">
          <span className="span1"></span>
          <span className="span2"></span>
          <span className="span3"></span>
        </div>
      </div>
    );
  }

  // ── Render a single desktop row ──────────────────────────────────────────
  const renderDesktopRow = (trade) => {
    const rowId   = getByPath(trade, idKey);
    const orders  = getByPath(trade, ordersKey) || [];
    const isMulti = orders.length > 1;
    const isExp   = isOpen(rowId);
    const helpers = { toggle, isOpen, orders, isMulti, isExp };

    return (
      <tr key={rowId}>
        {/* Multi-order badge */}

        {/* Clickable name cell */}
        {showNameCol && (
          <td
            style={{ width: "180px" }}
            className="tradeStatsSnapshot"
            onClick={() => onRowClick?.(trade)}
          >
            {getByPath(trade, nameKey)?.split("_")[0]}
          </td>
        )}

        {/* Dynamic columns */}
        {columns.map((col, colIndex) => {
          const cellContent = typeof col.render === "function"
            ? col.render(trade, helpers)
            : getByPath(trade, col.key) ?? "-";

          const expandedRows = col.expandable && isExp && orders.length > 0 && (
            <div className="tableDropdownContent">
              {orders.map((order, i) => (
                <p className="table-dropdown-option position-relative" key={i}>
                  {typeof col.expandRender === "function"
                    ? col.expandRender(order, trade)
                    : getByPath(trade, col.key)}
                </p>
              ))}
            </div>
          );

          return (
            <td key={colIndex} style={{ width: col.width || "auto", ...(col.style || {}) }}>
              {cellContent}
              {expandedRows}
            </td>
          );
        })}
      </tr>
    );
  };

  // ── Render a single mobile card ──────────────────────────────────────────
  const renderMobileCard = (trade) => {
    const rowId   = getByPath(trade, idKey);
    const orders  = getByPath(trade, ordersKey) || [];
    const isMulti = orders.length > 1;
    const isExp   = isOpen(rowId);
    const helpers = { toggle, isOpen, orders, isMulti, isExp };

    return (
      <div key={rowId} className="">
        <div className="tableMobView verifiedTrade">
          <div className="border-0 border-bottom tradeListingTableMobCard card">
            <div className="px-0 card-body">
              {showNameCol && (
                <p onClick={() => onRowClick?.(trade)} style={{ cursor: "pointer" }}>
                  {getByPath(trade, nameKey)?.split("_")[0]}
                </p>
              )}
              <div className="firstMobileRow">
                <div className="assetColumn">
                  <h6>Asset</h6>
                  {
                    typeof columns[1]?.render === "function"
                    ? columns[1].render(trade, helpers)
                    : getByPath(trade, columns[1]?.key) ?? "-"
                  }
                </div>
                {columns[2] && (
                  <div className={columns[2].mobileWrapperClass || "blockchainIdWrapper"}>
                    <div>
                      {typeof columns[2].render === "function"
                        ? columns[2].render(trade, helpers)
                        : <p style={{ wordBreak: "break-all" }}>{getByPath(trade, columns[2].key) ?? "-"}</p>}
                    </div>
                  </div>
                )}
              </div>
              <div className="secongMobileRow">
                <div>
                  <h6>Date</h6>
                  <p className="blockDate">
                    {typeof columns[0]?.render === "function"
                      ? columns[0].render(trade, helpers)
                      : getByPath(trade, columns[0]?.key) ?? "-"}
                  </p>
                </div>
                <div>
                  <h6>Time</h6>
                  {columns[3] && (
                    <div className={columns[3].mobileWrapperClass || "blockchainIdTime"}>
                      <div>
                        {typeof columns[3].render === "function"
                          ? columns[3].render(trade, helpers)
                          : <p style={{ wordBreak: "break-all" }}>{getByPath(trade, columns[3].key) ?? "-"}</p>}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  {columns[5] && (
                    <div className={columns[4].mobileWrapperClass || "blockchainIdWrapper"}>
                      <div>
                        {typeof columns[5].render === "function"
                          ? columns[5].render(trade, helpers)
                          : <p style={{ wordBreak: "break-all" }}>{getByPath(trade, columns[5].key) ?? "-"}</p>}
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="verifyBlockchainTradesWrapper">

      {/* ── Desktop ─────────────────────────────────────────────────────── */}
      <div style={{ position: "relative" }} className="d-md-block d-none">
        <div className="publicVerifiedTradesTableWrap">
          <table className="publicVerifiedTradesTable">
            <thead>
              <tr>
                {showNameCol && <th>{isCommunity ? "Community" : "Trader"}</th>}
                {columns.map((col, i) => (
                  <th key={i}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!trades.length ? (
                <tr>
                  <td colSpan={columns.length + 2}>
                    <div className="noDataTable">
                      {/* <img src={noDataIcon} alt="noDataIcon" /> */}
                      <h3 className="text-center">{noDataMessage}</h3>
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map(renderDesktopRow)
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div>
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* ── Mobile ──────────────────────────────────────────────────────── */}
      <div className="verifyBlockchainTradesMobileViewWrap d-md-none d-block">
        {!trades.length ? (
          <div className="noDataMobileView">
            {/* <img src={noDataIcon} alt="noDataIcon" /> */}
            <h3 className="text-center">{noDataMessage}</h3>
          </div>
        ) : (
          paginated.map(renderMobileCard)
        )}

        {totalPages > 1 && (
          <div>
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

    </div>
  );
};

export default VerifiedTradesTable;